import { GenericUtils } from '@unpispas/upp-defs';
import { SchemaTable, _schemaRelate } from './item';

import { ReplaySubject, Subject, Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import type { dataService } from '../data';
import type { aliveItems } from '../data';
import type { dataStorage } from '../data';

/**
 * Defines the structure for dependency information between objects.
 * Each key represents a dependency name, and the value describes the dependent object and its relation details.
 */
export type DependInfo = {
    [key: string]: {
        /** 
         * The dependent object.
         * Represents the object instance that this dependency relies on.
         */        
        item: BaseObject;

        /**
         * Details about the relationship between the current object and the dependent object.
         */        
        relation_info: {
            /**
             * The target table or type of the dependency.
             */            
            to: string | null;

            /**
             * The field used to establish the dependency.
             */
            by: string | null;
        };
    };
};

/******************************/
/* BASE CLASS                 */
/******************************/

/**
 * Represents configuration options for object instances.
 * These options define behaviors such as volatility and storage handling.
 */
export interface ObjectOptions {
    /**
     * Indicates if the object instance is volatile.
     * Volatile instances are not persisted in local storage.
     * Defaults to `false` if not specified.
     */    
    volatile?: boolean;

    /**
     * Indicates if the object is created in a partial context.
     */    
    partial?: boolean;

    /**
     * Specifies the storage instance to use for resolving existing relations.
     */    
    storage?: dataStorage;
}

/**
 * Represents the requirements information for this item to be commited
 */
export interface CommitRequires {
    /**
     * Infomation to access the related item
    */
    intofrom: any,
    
    /**
     * Information for the related item
    */
    relation: any,

    /**
     * The related item requirements (recursive definition)
     */
    requires: any,
}

/**
 * Represents the information required to commit the data to the server.
 */
export interface CommitChange {
    /**
     * Specifies the relation information (for this entry)
     */
    relation: any;

    /**
     * Specifies the entry information to be commited to the server
     */
    entrynfo: any;

    /**
     * Specifies this entry requirements that must be sattisfied 
     */
    requires: any;

    /**
     * This is the timestamp refetence for this change (set on commit)
     */
    reference?: number;
}

/**
 * Represents the base class for managing persistent objects and their relationships.
 * This abstract class provides methods for tracking changes, managing dependencies,
 * and handling storage-related logic. It is designed to be extended by specific implementations.
 */
export abstract class BaseObject {
    /** This object schema definition. */    
    protected _schema: SchemaTable | null = null;

    /** Subject for emitting commit events. */
    protected _onCommit = new Subject <void> ();
    /** Observable for listening to commit events. */
    public OnCommit = this._onCommit.asObservable();

    /** Indicates if changes need to be committed to the server. */    
    protected _toCommit = false;
    /** Indicates if changes have been provided to the server. */
    protected _inCommit = false;
    /** Indicates if this instance must be added to the server */
    protected _toInsert = false;
    /** Indicates if this instance must be updated in the server */
    protected _toUpdate = false;
    /** Indicates if this instance must be deleted from the server (hard deletion) */
    protected _toDelete = false;
    /** Indicates if this instance is used for registry notifications */
    protected _toNotify = false;
    /** Indicates if this instance has been loaded from the server. */
    protected _isLoaded = false;
    /** Pending local field edits since last commit payload capture (for OnUpdate redirty). */
    protected _lastchange: Array <{ property: string, value: any }> = [];

    /** Configuration options for this object instance. */
    protected _objoptions: ObjectOptions | null = null;
    /** Volatile storage for the object. */
    protected _storage: dataStorage | null = null;

    /** Indicates if this object is volatile and not persisted in the database. */    
    protected _isVolatile = false;
    /** Indicates if this object belongs to a partial context. */
    protected _isPartial = false;
    /** Completed marker from last reports/complete response for partial objects. */
    protected _completed: any | null = null;
    /** Indicates if this object is part of a catalog. */
    protected _isCatalog = false;

    /** A subject used to track the loading state of the object. */ 
    protected _waitLoaded = new ReplaySubject<boolean>(1);

    /******************************/
    /* ABSTRACT METHODS           */
    /******************************/

    /**
     * Abstract getter to determine if the object should be inserted.
     * @returns `true` if the object needs to be inserted, otherwise `false`.
     */    
    abstract get ToInsert(): boolean;

    /**
     * Abstract getter to determine if the object should be updated.
     * @returns `true` if the object needs to be updated, otherwise `false`.
     */
    abstract get ToUpdate() : boolean;

    /**
     * Abstract setter for setting data to the object.
     * @param value - The data to set.
     */    
    abstract set Data(value: any);

    /**
     * Abstract getter for retrieving the object's information.
     */
    abstract get Info();
    
    /**
     * Abstract setter for updating the object's information.
     * @param value - The new information to set.
     */    
    abstract set Info(value: any);

    /**
     * Abstract getter for obtaining the source object of this copy.
     * Represents the original `BaseObject` instance that this object is a copy of.
     * @returns `BaseObject` instance representing the source object, or `null` if no source exists.
     */
    abstract get CopyOf(): BaseObject | null;

    /**
     * Abstract setter for assigning the source object of this copy.
     * Sets the original `BaseObject` instance that this object is a copy of, or clears it by assigning `null`.
     * @param value - The `BaseObject` instance to set as the source, or `null` to clear the reference.
     */
    abstract set CopyOf(value: BaseObject | null);

    /**
     * Abstract method invoked when the object is resolved.
     */    
    abstract OnResolve() : void;

    /**
     * Refreshes the current object based on the originating table.
     * This method is triggered by an external object and optimizes the refresh process to update only the parts affected by changes from the specified table.
     * @param table - The name of the tables representing the origin of the refresh request.
     */
    abstract DoRefresh(table: string | string[], upwards: boolean) : void;
    
    /**
     * Abstract method to perform an upward refresh in the object hierarchy.
     * @param table - The name of the tables representing the origin of the refresh request.
     */
    abstract UpRefresh(table: string[], _updates: Set <DataObject> | null) : void;

    /******************************/
    /* STARTS HERE                */
    /******************************/

    protected _CreateStorage(): dataStorage {
        const StorageCtor = this.data.store.constructor as { new (): dataStorage };
        return new StorageCtor();
    }

    /**
     * Constructs a new instance of the `BaseObject` class.
     * @param table - The table associated with this object.
     * @param objid - The object identifier.
     * @param data - The data service used for operations.
     * @param objoptions - Configuration options for the object.
     */    
    constructor(public table: string, public objid: string | null, public data: dataService, objoptions: ObjectOptions){
        this.objid = objid == null ? null : String(objid);

        this._objoptions = objoptions || {};

        this._isVolatile = ('volatile' in this._objoptions) && (!!this._objoptions.volatile);
        this._isPartial = ('partial' in this._objoptions) && (!!this._objoptions.partial);
        this._storage = ('storage' in this._objoptions) && this._objoptions['storage'] ? this._objoptions['storage'] : null;

        this._completed = null;

        if (this._isPartial && !this._storage){
            this._storage = this._CreateStorage();
        }

        if (!this._isVolatile){
            this._toInsert = (this.objid == null);
        }
    }

    /**
     * Release the object resources. Override to provide custom behavior.
     */    
    protected OnDestroy(){
        // nothing to do
    }

    /******************************/
    /* OBJECT IDENTIFIERS         */
    /******************************/

    /**
     * Gets a reference ID for the object. Valid for object instance tracking.
     * @returns The unique reference ID.
     */    
    get refid(){
        return this._uuid;
    }

    /**
     * Gets a reference to the object ID or UUID. Valid for object comparison.
     * @returns The object reference.
     */    
    get objref(){
        return (this.objid && !isNaN(Number(this.objid))) ? String(this.objid) : this._uuid;
    }

    /**
     * Gets the catalog reference. Override in catalog objects.
     * @returns `null` by default.
     */    
    get ctgref(){
        return null;    // overriden in catalog objects
    }

    /**
     * Computes a hash based on the object's information, excluding certain fields.
     * @returns A string representing the hash.
     */    
    protected _simplehash: string | null = null;
    get hash(){
        if (!this._simplehash){
            const _info: any = {};
    
            for (const key in this.Info) {
                if (['objid', 'created', 'updated'].includes(key)){
                    continue;
                }
                _info[key] = String(this.Info[key]);
            }

            this._simplehash = GenericUtils.simpleHash(JSON.stringify(_info));
        }

        return this._simplehash;
    }

    /**
     * Indicates if this object is part of a catalog.
     */    
    get IsCatalog(): boolean{
        return this._isCatalog;
    }

    set IsCatalog(value: boolean){
        this._isCatalog = value;
    }

    /**
     * Checks if this object belongs to or can be added to the specified storage.
     * @param storage - The storage instance to check against.
     * @returns `true` if the object belongs to the storage or can be added; otherwise, `false`.
     */   
    ToStorage(storage: dataStorage){
        // check if trying to add to custom storage
        if (this._storage){
            return this._storage == storage;
        }

        if (this.IsPartial){
            console.warn("[STORAGE] Partial object cannot be added to main storage: " + this.table + "@" + this.objref);
            return false;
        }

        // we are trying to add to the main storage
        if (this.IsVolatile){
            console.warn("[STORAGE] Volatile object cannot be added to main storage: " + this.table + "@" + this.objref);
            return false;
        }

        return (this.data.store == storage)
    }

    /**
     * Provides the contextual storage for this object.
     */
    get Storage(): dataStorage {
        return this._storage || this.data.store;
    }

    /**
     * Indicates if this object belongs to a partial context.
     */
    get IsPartial(): boolean {
        return this._isPartial;
    }

    set IsPartial(value: boolean) {
        if (this._isPartial === value){
            return;
        }

        if (value){
            throw new Error("[ERROR] Partial mode can only be enabled at construction time");
        }

        if (this._completed == null){
            throw new Error("[ERROR] Cannot promote partial object while Completed is null");
        }

        if (this.objid){
            const _global = this.data.store.GetByRef(this.table, this.objid, null);
            if (_global && _global !== this){
                throw new Error("[ERROR] Partial promotion collision in global store for " + this.table + "@" + this.objid);
            }
        }

        this._isPartial = false;
        this._completed = null;
        this._storage = null;
        this.data.store.AddObject(this);
    }

    /**
     * Completed marker returned by reports/complete response.
     * For non-partial objects this value is always null.
     */
    get Completed(): any | null {
        if (!this.IsPartial){
            return null;
        }
        
        return this._completed;
    }

    set Completed(value: any | null) {
        if (!this.IsPartial){
            this._completed = null;
            return;
        }
        
        this._completed = (value == null) ? null : value;
    }

    /******************************/
    /* OBJECT PROPERTIES         */
    /******************************/

    /**
     * Read only. Indicates if the object is volatile.
     */    
    get IsVolatile(){
        return this._isVolatile;
    }

    /**
     * Read only. Indicates if the object is in a commit state.
     */    
    get InCommit(){
        return this._inCommit;
    }

    /**
     * Indicates if the object is fully loaded from the server.
     */
    get IsLoaded(): boolean {
        return this._isLoaded;
    }

    set IsLoaded(value: boolean) {
        this._isLoaded = value;
    }

    /**
     * Waits until the object is fully loaded.
     * 
     * This method returns a Promise that resolves once the object has been marked as loaded.
     * If the object is already loaded, it resolves immediately.
     * 
     * @returns A promise that resolves when the object is fully loaded.
     */
    async WaitLoaded(): Promise<void> {
        if (!this.ToInsert && !this._isLoaded){
            await firstValueFrom(this._waitLoaded.asObservable());
        }
    }

    /********************************/
    /* PERFORM DATA UPDATES         */
    /********************************/

    /**
     * Abstract method to handle updates to the object.
     * @param info - The data used for the update.
     * @returns `true` if the update is successful, otherwise `false`.
     */    
    protected abstract OnUpdate(info: any): boolean;   

    /**
     * Abstract method to handle updates to the object's internal state.
     * This method must be implemented by derived classes to define how the object
     * processes the provided data and applies changes to its properties.
     *
     * @param info - The new data to apply to the object.
     */    
    protected abstract _OnUpdate(info: any): void;

    /** Subject for notifying updates (data reloaded from server). */
    protected _onUpdated = new Subject<BaseObject>();
    /** Observable for listening to update notifications. */
    public OnUpdated = this._onUpdated.asObservable();

    /** Subject that emits when an overwrite operation is completed. */
    protected _onOverwrite = new Subject<BaseObject> ();
    /** Observable that emits the target object after a successful overwrite operation. */
    public OnOverwrite = this._onOverwrite.asObservable();

    /**
     * Forces an update for session instances. Default implementation logs an error.
     */    
    public DoUpdate(): void {
        console.error("[ERROR] Can only force updates for session instances");
    }

    /**
     * Propagates change information to the created object.
     * Updates the object state if the change applies to the same table.
     * @param change - An object containing the change details, including the affected table.
     */   
    public OnChange(change: any): void {
        if ((change['table'] == this.table) && this.OnUpdate(change)){
            this.DoRefresh(this.table, false);

            this._toInsert = false;
            this._toUpdate = false;
            this._toDelete = false;

            this._onUpdated.next(this);
        }
    }

    /********************************/
    /* COMMIT CHANGES TO SERVER     */
    /********************************/
    
    /**
     * Obtain the object dependencies that must exist for data integrity
     */      
    protected abstract get Depend(): DependInfo;

    /**
     * Obtain the children objects that must be commited within this object
     */      
    protected abstract get Children(): Array <BaseObject>;

    /**
     * Obtain the change fields that must be provided to the database in the server
     */      
    protected abstract get Change(): any;

    /**
     * Provides the relation information of this instance, for a related one
     */      
    public get Relation() {
        const _info: any = {
            table: this.table
        }

        if (this.objid){
            _info['objid'] = this.objid;
        }
        else {
            _info['_uuid'] = this._uuid;
        }

        if (!_info['objid'] && !_info['_uuid']){
            throw new Error("[ERROR] Invalid relation for commit (no objid nor uuid is avaibale)!")
        }

        return _info;
    }

    /**
     * Provides this instance requirements for data integrity.
     * Mutual FK graphs (e.g. QRCODE ↔ QRSCAN) must not recurse forever.
     */      
    public get Requires() {
        return this._BuildRequires(new Set <BaseObject>());
    }

    /**
     * Builds the recursive requires map with the same cycle control as `_CommitMap`.
     * When an object is reached again, nested `requires` is empty but the edge
     * (`intofrom` + `relation`) is still emitted by the caller.
     */
    private _BuildRequires(_seen: Set <BaseObject>): Record <string, CommitRequires> {
        if (_seen.has(this)){
            return {};
        }
        _seen.add(this);

        const _requires = new Map <string, CommitRequires>();
        for(const field in this.Depend){
            if (this.Depend[field]){
                const _depend = this.Depend[field];
                if (!_depend.item){
                    continue;   // no item provided
                }

                _requires.set(field, {
                    intofrom: _depend.relation_info,
                    relation: _depend.item.Relation,
                    requires: _depend.item._BuildRequires(_seen),
                });
            }
        }

        return Object.fromEntries(_requires);
    }

    /**
     * Completes the entry information to be processed during synchronization opertaion in the server
     */      
    public get Entrynfo(){
        return (async () => {
            let _entrynfo = await this.Change;
            if (typeof _entrynfo === "function"){
                _entrynfo = await _entrynfo();
            }

            if (_entrynfo){
                _entrynfo['table'] = this.table;
                _entrynfo['_uuid'] = this._uuid;

                if (this.IsVolatile){
                    _entrynfo['_vltl'] = true;
                }

                if (this._toUpdate){
                    _entrynfo['_actn'] = 'do_update';
                }

                if (this._toInsert){
                    _entrynfo['_actn'] = 'do_insert';
                }

                if (this._toDelete){
                    _entrynfo['_actn'] = 'do_delete';
                }            
    
                if (this._toNotify){
                    _entrynfo['_actn'] = 'do_regstr';
                }

                if (!('_actn' in _entrynfo)){
                    return null;    // already in commit!!
                }

                if (this.objid){
                    _entrynfo['objid'] = this.objid;
                }
            }
    
            return _entrynfo;    
        })();
    }

    /**
     * Builds a recursive map of the instances that must be committed for data integrity.
     * @param _commits - A map to store the objects and their changes.
     * Defaults to an empty map when not provided.
     * @returns A map containing the objects and their respective changes to commit.
     */    
    private _CommitMap(_commits = new Map <BaseObject, any>(), _items = new Set <BaseObject>): Map < BaseObject, Promise<CommitChange> > {
        // circular dependency control (item already included)
        if (_items.has(this)){
            return _commits;    
        }

        // ensure that the item is in the storage
        if ((!this._isLoaded) && (this._toInsert)){
            this.data.store.AddObject(this);  
        }

        // Mutated rows (`ToUpdate`/`ToDelete`) must enter the commit map even when
        // `!IsLoaded` (FK stubs); otherwise child updates such as I18NVALUE text
        // never reach sync (issue #39). `ToUpdate` without objid is converted to
        // insert by the setter, so these flags imply a server row.
        if ((this._toInsert || this._isLoaded || this._toUpdate || this._toDelete) && !this._isCatalog){
            _items.add(this);   

            // add this object changes and dependendencies
            if (this._toInsert || this._toUpdate || this._toDelete){
                this._toCommit = true;
                this._inCommit = true;

                if (!_commits.has(this)){   // avoid commiting the same entry
                    _commits.set(this, (async () => {
                        const _relation = await this.Relation;
                        const _entrynfo = await this.Entrynfo;

                        this._lastchange = [];

                        const _change: CommitChange = {
                            relation: _relation,
                            entrynfo: _entrynfo,
                            requires: await this.Requires    
                        }
    
                        // clear the update flags for this item
                        this._toInsert = false;
                        this._toUpdate = false;
                        this._toCommit = false;
    
                        return _change;
                    })())    
                }
            }

            // add any changes to the children
            for(const _child of this.Children){
                const _childcommits = _child._CommitMap(_commits, _items);
                for (const [_item, _change] of _childcommits) {
                    _commits.set(_item, _change);
                }
            }
        }

        return _commits;    
    }

    /**
     * Obtains the list of changes to be commited to the server
     */     
    get Commit(){
        const _commits = this._CommitMap();
        
        if (GenericUtils.VerboseLogging(1)){
            const _logchanges: any = {};
            for (const [ _item ] of _commits){
                _logchanges[_item.table] = (_item.table in _logchanges) ? _logchanges[_item.table] += 1 : 1;
            }

            console.info("[COMMIT] To be commited:");
            for(const _logchange in _logchanges){
                console.info(" - TABLE: " + _logchange + " (" + _logchanges[_logchange]+ " entries)");
            }
        }

        return Array.from(_commits.values());
    }

    /**
     * Commits changes to the server, resolving dependencies and ensuring data integrity.
     * @param force - If `true`, the commit is performed immediately; otherwise, it is queued for the next sync operation.
     * @returns A promise that resolves to `true` if the commit is successful, otherwise `false`.
     */  
    async DoCommit(force = false) : Promise <boolean> {
        const _changes: Promise <CommitChange> [] = this.Commit;
        if (_changes.length == 0){
            console.warn("[DATAOBJECT] Could not commit [" + this.table + "]. Nothing to be commited.");
        }
        else {
            await this.data.Commit(_changes);
            await this.data.Flush(force);    
        }

        this._onCommit.next();
        return true;
    }

    /********************************/
    /* FORCE COMMIT AND REFRESH     */
    /********************************/

    /**
     * Forces the object and all its children to be marked for an update.
     * This method recursively sets the `_toUpdate` flag on the current object and
     * all its child objects.
     */    
    public ForceUpdate(_updates: Set <BaseObject> | null = null){
        if (_updates == null){
            _updates = new Set <BaseObject>();
        }
        
        if (!_updates.has(this)){
            this._toUpdate = true;
            _updates.add(this);

            for (const _child of this.Children){
                _child.ForceUpdate(_updates);
            }    
        }
    }

    /**
     * Forces a commit of this object and all related children to the server.
     */    
    public async ForceCommit(){
        this.ForceUpdate();
        await this.DoCommit();
    }

    /**
     * Forces a refresh of this object's data from the server.
     * @returns A promise that resolves to `true` if the refresh was successful; otherwise, `false`.
     */    
    public ForceRefresh(){
        return new Promise((resolve) => {
            this.data.OnRefreshCompleted.pipe(take(1)).subscribe(
            () => {
                if (this.objid){
                    this.data.FetchByObjid(this.table, this.objid).then(
                    (data: any) => {
                        if (data['errorcode'] == 0){
                            this._OnUpdate(data['info']);
                        }
                        resolve(data['errorcode'] == 0);
                    });                
                }
                else {
                    resolve(false);
                }
            });        
        });
    }

    /********************************/
    /* INTERNAL METHODS             */
    /********************************/

    /** an unique identifier for this object */
    private __uuid: string | null = null;

    /** obtains the unique identifier for the object */    
    get _uuid() : string {
        if (!this.__uuid){
            this.__uuid = GenericUtils.uuidv4();
        }
        return this.__uuid;    
    }

    /** overrides the unique identifier of this object */    
    set _uuid(value: string){
        this.__uuid = value;
    }
}

/******************************/
/* RELATION INFORMATION       */
/******************************/

/**
 * Represents a related object with hierarchical relationships.
 * This class extends `BaseObject` and provides functionality for managing 
 * parent-child relationships, one-to-one and one-to-many associations, 
 * and their corresponding bidirectional relations.
 */
export abstract class RelatedObject extends BaseObject {
    /**
     * Depth of `OnResolve` / `OnReplace` unlink-relink. Nested `DoRefresh` from
     * `DelChild`/`AddChild` is skipped so subscribers never see an empty parent
     * list between `DelRelated` and `AddRelated` (insert ACK).
     */
    private static _deferRefreshDepth = 0;

    /**
     * Runs relation rewrite without emitting `OnRefresh` until `work` returns.
     */
    protected static DeferChildRefresh(work: () => void): void {
        RelatedObject._deferRefreshDepth++;
        try {
            work();
        }
        finally {
            RelatedObject._deferRefreshDepth--;
        }
    }

    /**
     * True while `DeferChildRefresh` is active on this call stack.
     */
    protected static get IsChildRefreshDeferred(): boolean {
        return RelatedObject._deferRefreshDepth > 0;
    }

    /**
     * Stores the relationships of this object with its children.
     * Each key represents a relation name, and the value can be:
     * - A single `RelatedObject` for one-to-one relationships.
     * - A map of `RelatedObject` indexed by their unique reference for one-to-many relationships.
     * - `null` if no child exists for the relation.
     */    
    public _children: { [key: string]: RelatedObject | { [key: string]: RelatedObject } | null } = {};     
    
    /**
     * Stores the children in an array format for performance optimization.
     * Each key represents a relation name, and the value is an array of `RelatedObject`.
     */    
    public _chldlist: { [key: string]: RelatedObject[] } = {};                         

    /**
     * Stores the bidirectional relationships between this object and other `RelatedObject` instances.
     * The map uses `RelatedObject` instances as keys and a `Set` of relationship fields as values.
     */    
    protected _related: Map<RelatedObject, Set<string>> = new Map();

    /**
     * Constructs a new `RelatedObject` instance.
     * @param table - The table associated with this object.
     * @param objid - The unique identifier of the object.
     * @param data - The data service used for managing this object.
     * @param objoptions - Configuration options for this object.
     */    
    constructor(table: string, objid: string | null, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
    }

    /**
     * Override in DataObject to emit when a relation's children list changes (AddChild/DelChild/SetChild).
     * Views can subscribe to OnChildChanged and re-register subscriptions to the new child set.
     */
    protected DoChildChanged(_target: string): void {
        void _target;
    }

    /**
     * Hook invoked when a one-to-one relation is linked or unlinked through `SetChild`.
     * Default implementation does nothing; domain objects override it to react to specific
     * relation changes without leaking business logic into the base class.
     * @param _relation - The relationship field that changed, or null.
     * @param _related - The related object linked or unlinked, or null.
     */
    protected DoChildLinked(_relation: string | null, _related: RelatedObject | null): void {
        void _relation;
        void _related;
    }

    /********************************/
    /* DETERMINE RELATIONS          */
    /********************************/

    /**
     * Ensures a child exists in storage and retrieves it.
     * If the child is not found in the main storage, it attempts to add it.
     * @param child - The child object to be added.
     * @returns The `RelatedObject` instance of the child.
     */    
    private _AddChild(child: RelatedObject): RelatedObject{
        const _global = this.data.store.GetObject(child) as RelatedObject | null;
        if (_global){
            return _global;
        }

        if (child.IsVolatile && !child._storage){
            return child;
        }

        const _storage = this._storage || child._storage || this.data.store;
        if (!_storage.AddObject(child)){
            child = _storage.GetObject(child) as RelatedObject;
        }

        return child;
    }

    /**
     * Sets a one-to-one relationship with a child.
     * If a previous child exists for the same target, it will be removed.
     * @param target - The relation name to associate with the child.
     * @param child - The child to set.
     * @param relation - The field representing the relationship.
     * @returns `true` if the relationship changes, otherwise `false`.
     */    
    SetChild(target: string, child: RelatedObject | null, relation: string): boolean {
        if (relation && (target != relation) && (relation in this.Info)){
            console.warn("[WARNING]: Field names mismatch for children ('" + target + "') and relation ('" + relation + "')");
        }

        let _change = false;

        if (!(target in this._children)){
            this._children[target] = null;
        }

        // remove previous existing relation 
        const _prev = this._children[target];
        if (_prev && !(_prev instanceof RelatedObject)){
            throw new Error("[ERROR] Setting unique child to an existing children entry!")
        }

        if (_prev){
            _change = (this.DelRelation(target, _prev, relation)) && (!child);
            this._children[target] = null;
            this.DoChildLinked(relation, _prev);
        }

        // create the relation with the new object
        if (child){
            child = this._AddChild(child);
            _change = this.AddRelation(target, child, relation) || _change;
            this._children[target] = child;
            this.DoChildLinked(relation, child);
        }

        if (_change){
            this.DoRefresh(child ? child.table: this.table, true);
            this.DoChildChanged(target);
        }

        return _change;
    }

    /**
     * Adds a child to a one-to-many relationship.
     * @param target - The relation name to associate with the child.
     * @param child - The child to add.
     * @param relation - The field representing the relationship.
     */    
    AddChild(target: string, child: RelatedObject | null, relation: string | null): void {
        let _change = false;

        if (!(target in this._children)){
            this._children[target] = null;
        }

        if (child){
            const _child = this._AddChild(child);

            _change = this._InsertChild(target, _child);
            if (_change){
                this.AddRelation(target, _child, relation);
            }

            if (_change){
                this.DoRefresh(child.table, true);
                this.DoChildChanged(target);
            }
        }
    }

    /**
     * Removes a child from a relationship.
     * For one-to-one relationships, the child is set to `null`.
     * For one-to-many relationships, the specific child is removed.
     * @param target - The relation name associated with the child.
     * @param child - The child to remove.
     * @param relation - The field representing the relationship.
     */    
    DelChild(target: string, child: RelatedObject, relation: string): void { 
        let _change = false;

        if (this._children[target] instanceof RelatedObject){
            if (!child){
                child = this._children[target]
            }

            _change = this.DelRelation(target, child, relation);
            this._children[target] = null;
        }
        else {
            if (child){
                _change = this._DeleleChild(target, child);
                if (_change){
                    this.AddRelation(target, child, relation);
                }
            }
        }

        if (_change){
            this.DoRefresh(child.table, true);
            this.DoChildChanged(target);
        }
    }

    /**
     * Sets multiple children for a one-to-many relationship.
     * This method associates an array of `DataObject` instances with the specified relation name.
     * If the array of children is empty, no changes are made to the relationship.
     *
     * When `relation` is set (schema `by`), also points each child's 1:1 FK back to this parent
     * using the child's `>` relate (`name` / `reason` / `by`) when present (RF-5).
     * `AddChild` stays one-sided; completing the parent-asserted membership is `SetChildren`' job.
     * 
     * @param target - The relation name to associate with the children.
     * @param children - An array of `DataObject` instances to associate as children.
     * @param relation - The child FK field for this relationship (schema `by`), or empty.
     */
    SetChildren(target: string, children: RelatedObject[], relation: string){
        for(const _child of children){
            this.AddChild(target, _child, relation);

            if (!relation){
                continue;
            }

            const _back = (_child._schema?.relate || []).find(
                (entry: { direction: string; by: string; name: string; reason: string }) =>
                    entry.direction === '>' && (entry.by === relation || entry.name === relation)
            );

            const _childTarget = _back?.name || relation;
            const _childReason = _back?.reason || _childTarget;
            const _childBy = _back?.by || relation;

            if (_child._children[_childReason] !== this){
                _child.SetChild(_childTarget, this, _childBy);
                _child._toUpdate = false;   // database is already updated
            }
        }
    }

    /********************************/
    /* RELATE OBJECTS               */
    /********************************/

    /**
     * Validates if a value is a map of children.
     * @param value - The value to check.
     * @returns `true` if the value is a map of children, otherwise `false`.
     */    
    private isChildMap(value: any): value is { [key: string]: RelatedObject } {
        if ((value == null) || (value instanceof RelatedObject)){
            return false;
        }

        return (typeof value === 'object');
    }
        
    /**
     * Retrieves the unique key for a child within a relation.
     * @param target - The relation name.
     * @param child - The child to find.
     * @returns The unique key if found, otherwise `null`.
     */    
    private _ChildKey(target: string, child: RelatedObject){
        if ((target in this._children) && this._children[target]){
            if (child.objid && (child.objid in this._children[target])){
                return child.objid;
            }
    
            if (child._uuid && (child._uuid in this._children[target])){
                return child._uuid;
            }

            if (child.ctgref && (child.ctgref in this._children[target])){
                return child.ctgref;
            }
        }

        return null;
    }

    /**
     * Inserts a child into a one-to-many relationship.
     * @param target - The relation name.
     * @param child - The child to insert.
     * @returns `true` if the child was inserted, otherwise `false`.
     */    
    private _InsertChild(target: string, child: RelatedObject){
        const _key = this._ChildKey(target, child);
        if (!_key){
            if (!this._children[target] || (this.isChildMap(this._children[target]))){
                if (!this._children[target]){
                    this._children[target] = {};
                }

                this._children[target][child.objref] = child;
                this._chldlist[target] = Object.values(this._children[target] || []);            
            }
            else {
                throw new Error("[ERROR] Trying to insert a child in a non-list entry!")
            }

            return true;
        }

        return false;
    }

    /**
     * Deletes a child from a one-to-many relationship.
     * @param target - The relation name.
     * @param child - The child to delete.
     * @returns `true` if the child was deleted, otherwise `false`.
     */    
    private _DeleleChild(target: string, child: RelatedObject){
        const _key = this._ChildKey(target, child);
        if (_key){
            if (this.isChildMap(this._children[target])){
                delete this._children[target][_key];
                this._chldlist[target] = Object.values(this._children[target] || []);
            }
            else {
                throw new Error("[ERROR] Trying to delete a child in a non-list entry!")
            }

            return true;
        }

        return false;
    }

    /* NOTE:
       Object relations is -> map of (RelatedObject, set of RelationField) 
    */

    /**
     * Adds relationship information for a specific direction and target.
     * This method manages bidirectional relationships and updates object references.
     * @param direction - The relationship direction ('>' for outgoing, '<' for incoming).
     * @param name - The name of the relationship field.
     * @param related - The related object to establish relationship with.
     * @returns True if the relationship information was added and caused changes, false otherwise.
     */       
    private AddRelationInfo(direction: '>' | '<', name: string, related: RelatedObject): boolean {
        let _change = false;

        const _relation = direction + ' ' + name;
        const _existing = this._related.get(related);
        if (_existing){
            _existing.add(_relation);
        }
        else {
            this._related.set(related, new Set([_relation]));
        }

        /*
            NOTE: 
            Applies when a related item is stored into the 'Info" fields for an item: 
            - The target (in where the object is stored) and the relation must match
            - Otherwise we cannot determine if it is an stored or a related item
        */

        if (this.IsLoaded && (direction == '>')){
            if (!(name in this.Info)){
                if (!(name in this._children)){     // source item do not stores the objids -> check if its valid as children
                    console.warn("[WARNING]: field '" + name + "' not found in target item definition")
                }
            }
            else {  // the relation must be updated within the stored data
                const _relobjid = (related && related.objid) ? related.objid : null;
                if (_relobjid == null) {    // related object without objid (keep the instance)
                    _change = true;
                    if (_change && (this.Info[name] != null)) {
                        this.Info[name] = null;  
                        this._toUpdate = true;
                    }
                }
                else {
                    _change = (this.Info[name] != _relobjid);
                    if (_change && (this.Info[name] != _relobjid)) {
                        this.Info[name] = _relobjid;
                        this._toUpdate = true;
                    }    
                }
            }
        }

        return _change;
    }

    /**
     * Removes relationship information for a specific direction and target.
     * This method cleans up bidirectional relationships and updates object references.
     * @param direction - The relationship direction ('>' for outgoing, '<' for incoming).
     * @param name - The name of the relationship field.
     * @param related - The related object to remove relationship from.
     * @returns True if the relationship information was removed and caused changes, false otherwise.
     */    
    private DelRelationInfo(direction: '>' | '<', name: string, related: RelatedObject): boolean {
        let _change = false;

        const _existing = this._related.get(related);
        if (_existing){
            for (const _relation of _existing){
                if (_relation == direction + ' ' + name){
                    _change = (!!this.Info[name]);
                    if (_change){
                        this.Info[name] = null;   // clear the objid for the related field
                        this._toUpdate = true;
                    }

                    _existing.delete(_relation);
                    if (_existing.size == 0){
                        this._related.delete(related);
                        if (this._related.size == 0){
                            this.OnDestroy();   // this item is isolated (no relations)
                        }
                    }
                }
            }
        }

        return _change;
    }

    /**
     * Establishes a bidirectional relationship between this object and another.
     * This method creates both outgoing and incoming relationship references.
     * @param fromrelation - The field name for the outgoing relationship.
     * @param to - The target object to relate to.
     * @param childrelation - The field name for the incoming relationship, or null if none.
     * @returns True if the relationship was established and caused changes, false otherwise.
     */    
    private AddRelation(fromrelation: string, to: RelatedObject, childrelation: string | null){
        let _change = false;

        // this must have an outgoing '<' relation to the destination
        _change = this.AddRelationInfo('>', fromrelation, to) || _change;

        // destination will have an incoming '>' relation from the source
        if (childrelation){
            _change = to.AddRelationInfo('<', childrelation, this) || _change;
        }

        return _change;
    }
  
    /**
     * Removes a bidirectional relationship between this object and another.
     * This method removes both outgoing and incoming relationship references.
     * @param fromrelation - The field name for the outgoing relationship.
     * @param to - The target object to remove relationship from.
     * @param childrelation - The field name for the incoming relationship, or null if none.
     * @returns True if the relationship was removed and caused changes, false otherwise.
     */    
    private DelRelation(fromrelation: string, to: RelatedObject, childrelation: string | null){    
        let _change = false;

        // this has an 1:N (this '<' to) relation to the destination
        _change = this.DelRelationInfo('>', fromrelation, to) || _change;

        // destination will have a 1:1 (to '>' this) relation from the source 
        if (childrelation){
            _change = this.DelRelationInfo('<', childrelation, this) || _change;
        }

        return _change;    
    }

    /********************************/
    /* RESOLVE OBJECTS              */
    /********************************/
    
    /**
     * Resolves relationships for this object.
     * Removes existing relationships, processes the object, and restores the relationships.
     */    
    OnResolve(){
        RelatedObject.DeferChildRefresh(() => {
            const _refs = this.DelRelated();
            this.data.store.Resolve(this);
            this.AddRelated(_refs);
        });

        this.DoRefresh(this.table, true);
    }

    /**
     * Replaces the current object in the storage system with a new identifier.
     * Updates all relationships accordingly.
     * @param uuid - The new unique identifier for this object.
     */    
    OnReplace(uuid: string){
        RelatedObject.DeferChildRefresh(() => {
            const _refs = this.DelRelated();
            this.data.store.Replace(this, uuid);
            this.AddRelated(_refs);
        });

        this.DoRefresh(this.table, true);
    }

    private _DelChild(from: RelatedObject, relation: string){
        let _fr: RelatedObject | null = null;
        let _to: RelatedObject | null = null;

        switch(relation.slice(0,1)){
            case '>':
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                _fr = this;
                _to = from;
                break;

            case '<':
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                _to = this;
                _fr = from;
                break;
        }

        const _deleteinfo: {
            from: RelatedObject | null;
            from_field: string | null;
            child_field: string;        
            restore: 'SetChild' | 'AddChild' | null
        } = {
            from: _fr,              // the parent item
            from_field: null,       // the field from the parent in where the child is removed
            child_field: relation,  // the field from the child that references to the parent
            restore: null           // SetChild / AddChild 
        };

        if (!_fr){
            return null;    // from not provided
        }

        for(const _relation in _fr._children){
            if (_fr._children[_relation] instanceof RelatedObject){
                if (_fr._children[_relation] == _to){
                    _deleteinfo.restore = 'SetChild';
                    _deleteinfo.from_field = _relation;

                    _fr.DelChild(_relation, this, relation.slice(2));
                    return _deleteinfo;         // child has been deleted
                }
            }
            else {
                for(const objref in _fr._children[_relation]){
                    if (_fr._children[_relation][objref] == _to){
                        _deleteinfo.restore = 'AddChild';
                        _deleteinfo.from_field = _relation;

                        _fr.DelChild(_relation, this, relation.slice(2));
                        return _deleteinfo;     // child has been deleted
                    }
                }
            }
        }

        return null;    // child not found
    }

    /**
     * Removes all relationships associated with this object.
     * @returns An array containing information about the removed relationships.
     */    
    protected DelRelated(){
        if (!this._schema){
            return;
        }

        // remove all incoming relations to this item
        const _delete = [];        
        for (const relation of this._schema.relate) {
            if (relation.child == false){   // only parents
                const object = this._children[relation.reason];
                if (object){
                    if (!(object instanceof RelatedObject)){
                        throw new Error("[ERROR] parent relation cannot be a list!");
                    }
    
                    const _deleteinfo = this._DelChild(object, relation.reason);
                    if (_deleteinfo){
                        _delete.push(_deleteinfo);  // do not return bi-directional relations
                    }                    
                }
            }
        }

        return _delete;
    }

    /**
     * Restores relationships that were previously removed.
     * @param _deleted - An array containing information about the relationships to restore.
     */    
    protected AddRelated(_deleted: any){
        let _fr = null;
        let _to = null;

        for(const _ref of _deleted){
            switch(_ref.child_field.slice(0, 1)){
                case '<':
                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    _to = this;
                    _fr = _ref.from;
                    break;

                case '>':
                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    _fr = this;
                    _to = _ref._from;
                    break;
            }

            // obtain the object from storage (in case has been replaced)
            _fr = this.data.store.GetObject(_fr) || _fr;
            _to = this.data.store.GetObject(_to) || _to;

            const _child_field = _ref.child_field.slice(2);
            if (_ref.restore == 'SetChild'){
                _fr.SetChild(_ref.from_field, _to, _child_field)
            }

            if (_ref.restore == 'AddChild'){
                _fr.AddChild(_ref.from_field, _to, _child_field)
            }
        }
    }
}

/******************************/
/* BASE CLASS FOR ITEM VIEWS  */
/******************************/

export type ViewType<T, U> = T & {
    [K in keyof U as K extends keyof T ? never : K]: U[K];
};

/**
 * Abstract base class representing a view wrapper for a `DataObject`.
 * 
 * `ViewObject` enhances the base data object (`T`) by providing additional functionality
 * such as proxying properties and integrating with a data service.
 * 
 * This class is designed to be extended to create specific view models that enhance
 * the data objects with additional computed properties and event subscriptions.
 *
 * @template T - The type of `DataObject` being wrapped by the view.
 */
export abstract class ViewObject<T extends DataObject> {
    /**
     * Returns a Proxy instance that allows seamless access to both `ViewObject` 
     * properties and properties of the underlying `_dataobject`.
     * 
     * If a requested property exists in the `ViewObject`, it is returned directly.
     * Otherwise, it is retrieved from `_dataobject`. If the property is a function,
     * it is properly bound to `_dataobject` to ensure correct execution context.
     * 
     * @returns {this & T} A Proxy object that merges `ViewObject` and `_dataobject` properties.
     */    
    get Proxy(): this & T {
        return new Proxy(this, {
            get: (target, prop, receiver) => {
                // avoid recursive calls to View property
                if (prop === "View") {
                    return undefined;
                }
    
                // direct return of ViewObject property when exists
                if (prop in target) {
                    return Reflect.get(target, prop, receiver);
                }
    
                // return the property from the _dataobject
                if (prop in target.dataobject) {
                    const value = target.dataobject[prop as keyof T];
                    if (typeof value === "function") {
                        return value.bind(target.dataobject);
                    }
                    return value;
                }
    
                return undefined;
            },

            set: (target, prop, value, receiver) => {
                void (receiver);
                
                // execute ViewObject property when exists
                if (prop in target) {
                    Reflect.set(target, prop, value);
                    return true;
                }

                // asing to dataobject when property is defined
                if (prop in target.dataobject) {
                    (target.dataobject as any)[prop] = value;
                    return true;
                }
    
                // otherwise asing into ViewObject
                (target as any)[prop] = value;
                return true;
            },

            has: (target, prop) => {
                return prop in target || prop in target.dataobject;
            },

            deleteProperty: (target, prop) => {
                if (prop in target.dataobject) {
                    return delete (target.dataobject as any)[prop];
                }
                return delete (target as any)[prop];
            }
        }) as this & T;
    }


    /** Subject that emits void events when the view needs to be refreshed. */
    private _onViewRefresh = new Subject <void> ();    
    /** Observable that emits notifications whenever the view must be refreshed. */
    public OnViewRefresh = this._onViewRefresh.asObservable();

    /** Subject that emits void events when the model needs to be refreshed. */
    private _onDataRefresh = new Subject <void> ();
    /** Observable that emits notifications whenever the model must be refreshed. */
    public OnDataRefresh = this._onDataRefresh.asObservable();

    private _scheduled = false;
    
    /** Triggers a refresh event for the view in an asynchronous manner. */    
    private _doRefreshView(mode: 'DATA' | 'VIEW'){
        switch(mode){
            case 'DATA':
                this._onDataRefresh.next();
                break;
            case 'VIEW':
                if (!this._scheduled) {
                    this._scheduled = true;
            
                    Promise.resolve().then(() => {
                        this._onViewRefresh.next();
                        this._scheduled = false;
                    });    
                }
                break;
        }
    }

    protected _tick_subscription: Subscription | null = null;

    /**
     * Schedules a view refresh operation.
     * @param now - If false, the refresh is scheduled to occur on the next tick of the clock.
     */    
    protected DoRefreshView(now = false){
        this._doRefreshView('DATA');   

        if (GenericUtils.DelayedRefresh() == false){
            now = true;     // forces inmediate refresh
        }

        if (now && this.data.clock.Enable){
            this._doRefreshView('VIEW');      
        }
        else {
            if (this._tick_subscription == null){
                this._tick_subscription = this.data.clock.OnRefreshTick.subscribe(
                () => {
                    this._tick_subscription?.unsubscribe();
                    this._tick_subscription = null;

                    this._doRefreshView('VIEW');      
                });
            }
        }
    }

    protected _subscriptions: Subscription[] = [];

    /**
     * Creates an instance of `ViewObject`.
     *
     * @param {T} dataobject - The data object instance to be wrapped.
     * @param {dataService} data - The data service used for managing data interactions.
     */  
    constructor(protected dataobject: T, protected data: dataService) {
        // nothing to do
    }    

    /**
     * Method for cleanup operations.
     * Should be called when the object is being destroyed to release resources.
     */    
    OnDestroy(): void {
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];        
    }

    /**
     * Abstract method for registering child objects or subscriptions.
     * This method must be implemented in derived classes.
     */    
    abstract doRegister(): void;

    /**
     * Retrieves the underlying data object wrapped by this view.
     * Will always return the latest object stored in the storage.
     * @returns {T} The base data object instance.
     */    
    get object(): T {
        return (this.data.store.GetObject(this.dataobject) as T) || this.dataobject;
    }

    /**
     * Retrieves the collection of active (alive) items managed by the data service.
     *
     * @returns {aliveItems} The active items registry.
     */    
    protected get alives(): aliveItems{
        return this.data.alives;
    }

    /**
     * Checks if the underlying data object is an instance of the specified type.
     * 
     * This method performs a runtime type check to determine if the wrapped data object
     * is an instance of the class type passed as parameter.
     *
     * @template T - The constructor type to check against
     * @param {T} typeConstructor - The constructor function of the class to check
     * @returns {boolean} True if the underlying object is an instance of the specified type, false otherwise
     * 
     * @example
     * // Check if the view wraps a Product object
     * if (view.ViewOf(Product)) {
     *   // Handle product-specific logic
     * }
     */    
    ViewOf<T extends new (...args: any[]) => any>(typeConstructor: T): boolean {
        return this.object instanceof typeConstructor;
    }
}

/******************************/
/* BASE FOR MODEL INSTANCES   */
/******************************/

/**
 * Represents a data object that extends the functionality of `RelatedObject`.
 * This class adds capabilities for managing object states, creating deep copies,
 * handling relationships, and synchronizing updates with a server.
 */
export abstract class DataObject extends RelatedObject { 
    /** Subject for emitting refresh events. */
    protected _onRefresh = new Subject <Array<string>> ();
    /** Observable for listening to refresh events. */
    public OnRefresh = this._onRefresh.asObservable();

    /** Subject for emitting when a relation's children list changes (AddChild/DelChild/SetChild). */
    protected _onChildChanged = new Subject<string>();
    /** Emits the relation name when the set of children for that relation changes. */
    public OnChildChanged = this._onChildChanged.asObservable();

    protected override DoChildChanged(target: string): void {
        if (RelatedObject.IsChildRefreshDeferred){
            return;
        }

        this._onChildChanged.next(target);
    }

    /**
     * Creates an instance of `DataObject`.
     * @param table - The database table associated with this object.
     * @param objid - The object identifier.
     * @param data - The data service used for managing this object.
     * @param objoptions - Configuration options for the object.
     */    
    constructor(table: string, objid: string | null, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
    }

    /**
     * Completes this object when it belongs to a partial context.
     *
     * @returns `true` if object reaches complete state.
     */
    async ToComplete(): Promise<boolean> {
        if (!this.IsPartial || !this.objid){
            throw new Error('[ERROR] ToComplete is only available for partial objects with an objid');
        }

        await this.data.FetchByItem(this.table, [ this.objid ], this.Storage, this.Completed ?? 0);
        return this.Completed != null;
    }

    /**
     * Determines if this object should be inserted into the server.
     * @returns `true` if the object needs insertion; otherwise, `false`.
     */    
    get ToInsert(){
        return !this._isVolatile && !this._toCommit && !this._inCommit && !this._isCatalog && this._toInsert;
    }

    /**
     * Determines if this object hos been commited into the database.
     * @returns `true` if the object existis in the database; otherwise, `false`.
     */      
    get Commited(){
        if (this.objid || this.Info['objid']){
            return true;    // if it has an objid it is in the database
        }

        return false;
    }

    /**
     * Determines if this object should be updated in the server.
     * @returns `true` if the object needs an update; otherwise, `false`.
     */    
    get ToUpdate(){
        return !this._toCommit && !this._inCommit && !this._isCatalog && this._toUpdate && !this._toInsert;
    }

    /**
     * Marks this object for an update in the server.
     * @param value - `true` to mark the object for an update, otherwise `false`.
     */    
    set ToUpdate(value){
        this._toUpdate = value;
        if (this._toUpdate){
            this._toInsert = !this.objid;
            if (this._toInsert){
                this._toUpdate = false;
            }
        }

        this.DoRefresh(this.table, false);
    }    
    
    /**
     * Indicates whether this object is in the process of being committed to the server.
     * @returns `true` if the object is marked for commit; otherwise, `false`.
     */    
    get ToCommit(){
        return this._toInsert || this._toCommit || this._inCommit;
    }

    /**
     * Indicates whether this object should send notifications to the server.
     * @returns `true` if notifications are required; otherwise, `false`.
     */    
    get ToNotify(){
        return this._toNotify;
    }

    /**
     * Marks this object to send notifications to the server.
     * @param value - `true` to enable notifications, otherwise `false`.
     */    
    set ToNotify(value){
        this._toNotify = value;
    }

    /********************************/
    /* ACCESS TO VIEW ITEM          */
    /********************************/

    private _objectviewref: WeakRef<ViewObject<DataObject>> | null = null;

    /**
     * Retrieves or initializes the `ViewObject` instance associated with this `DataObject`.
     * This method ensures that only one instance of `ViewObject` is created per `DataObject`.
     * 
     * NOTE:
     * - This accessor is intended for internal lifecycle/registry usage (mainly `alives`).
     * - Prefer obtaining views through `data.alives.get(...)` in application code to avoid
     *   bypassing the canonical view registry.
     * 
     * - If the `ViewObject` has not been initialized (`null`), it is assigned using `dataView`.
     * - Once initialized, it always returns the same instance.
     *
     * @returns {ViewObject<DataObject> | null} The `ViewObject` instance associated with this object.
     */
    get View(): ViewObject<DataObject> | null {
        let view = this._objectviewref?.deref() ?? null;

        if (view == null) {
            view = this.viewProxy;
            if (view){
                this._objectviewref = new WeakRef(view);
            }
        }
    
        return view;        
    }

    /**
     * Getter that must be implemented by derived classes to provide the `ViewObject` instance.
     * This ensures that each specific `DataObject` implementation can define its own `ViewObject` type.
     *
     * - Each subclass should override this getter to return the correct `ViewObject` instance.
     * - The returned `ViewObject` will be assigned to `_objectview` upon first access to `View`.
     *
     * @returns {ViewObject<DataObject> | null} The instance of `ViewObject` corresponding to the concrete implementation.
     */    
    get viewProxy() : ViewObject<DataObject> | null {
        return null;
    };

    /********************************/
    /* CREATE DEEP COPIES           */
    /********************************/

    /** Reference to the original object this instance is a copy of. */
    protected _copyof: DataObject | null = null;

    /**
     * Determines if this object is a copy of the specified object.
     * @param object - The `DataObject` to compare against.
     * @returns `true` if this object is a copy of the specified object; otherwise, `false`.
     */    
    IsCopyOf(object: DataObject){
        if (this == object){
            return true;    // same product
        }
        else {
            if ((this._copyof != null) && (this._copyof == object)){
                return true;
            }
    
            if ((this._copyof != null) && (this._copyof == object._copyof)){
                return true;
            }
    
            if ((object._copyof != null) && (object._copyof == this)){
                return true;
            }
    
            return false;    
        }
    }

    /**
     * Finds an existing copy of the specified target object in the store,
     * or creates a new copy if the object is marked for duplication.
     *
     * If the target is already in the store or is identified as a copy,
     * that instance is returned. Otherwise, if the target is listed in `objnfo`,
     * a deep copy is created. If not, the original target is returned.
     *
     * @param target - The `DataObject` to find or copy.
     * @param store - A list used to track copied objects and avoid duplication.
     * @param objnfo - A set of objects marked for deep copy.
     * @returns A `DataObject` copy if needed, or the original object otherwise.
     */   
    /**
     * Locates the copy-set member that represents a given table + objid.
     *
     * The copy set (`objnfo`) holds the canonical originals scheduled for deep copy.
     * This resolves a row identity (table + objid) to that canonical instance, which
     * is the single source of truth even when a duplicate instance of the same row
     * exists elsewhere in memory.
     *
     * @param table - Table of the row to locate.
     * @param objid - Object id of the row to locate.
     * @param objnfo - Set of objects marked for deep copy.
     * @returns The canonical member sharing the table + objid, or `null` when none.
     * @protected
     */
    protected _FindById(table: string, objid: string, objnfo: Set <DataObject>): DataObject | null {
        for(const _object of objnfo){
            if ((_object.table === table) && (_object.objid === objid)){
                return _object;
            }
        }

        return null;
    }

    protected _FindCopy(target: DataObject, store: Array<DataObject>, objnfo: Set <DataObject>): DataObject | null {
        if (!target){
            return null;
        }

        // check if it is in store
        for(const _object of store){
            if ((_object == target) || (_object.IsCopyOf(target))) {
                return _object;     // already exists
            }
        }

        // check if copy is required
        if (objnfo.has(target)){
            return target._Copy(store, objnfo);
        }

        // canonical match by objid: a child:false (upward) reference can point at a
        // different instance than the one held inside the copy set when the ancestor
        // was loaded or synced separately. Instance identity then fails, so resolve
        // the duplicate to the copy-set member sharing its table + objid and copy that
        // one instead (keeping the precondition that same table + objid is one row).
        if (target.objid){
            const _canonical = this._FindById(target.table, target.objid, objnfo);
            if (_canonical && (_canonical !== target)){
                return this._FindCopy(_canonical, store, objnfo);
            }
        }

        return target;    // not found in store
    }
    
    /**
     * Copies relationship data from this object to the target object,
     * based on the provided relation schema.
     *
     * Handles both parent and child relations:
     * - For parent relations, the related object is linked directly.
     * - For child relations, related objects are copied if required.
     *
     * @param target - The `DataObject` receiving the relation copy.
     * @param relation - The schema definition of the relation to be copied.
     * @param store - A list to track copied objects and avoid duplication.
     * @param objnfo - A set of objects marked for deep copy.
     * @private
     */   
    private _CopyRelation(target: DataObject, relation: _schemaRelate, store: Array<DataObject> = [], objnfo: Set <DataObject>): void {
        const _related = this._children[relation.reason];
        if (_related) {
            if (_related instanceof DataObject) {
                const _copy = this._FindCopy(_related, store, objnfo);
                target.SetChild(relation.reason, _copy, relation.by)
            }
            else {
                for (const _related of this._chldlist[relation.reason] as DataObject[]){
                    const _copy = this._FindCopy(_related, store, objnfo);
                    if (_copy){
                        target.AddChild(relation.reason, _copy, relation.by)
                    }
                }
            }
        }
        else if (relation.child === false) {
            // The upward 1:1 reference is held only as a stored objid in Info because the
            // relation was never materialized as an instance. Only a raw objid (string)
            // needs remapping here: a null means there is no related ancestor, and an
            // already materialized instance is handled by the branch above.
            const _refobjid = this.Info ? this.Info[relation.name] : null;
            if (typeof _refobjid !== 'string'){
                return;
            }

            // remap to the copied ancestor when it belongs to the copy set (objnfo holds
            // the originals); ancestors outside the copy set keep their stored objid.
            const _object = this._FindById(relation.target, _refobjid, objnfo);
            if (_object){
                const _copy = this._FindCopy(_object, store, objnfo);
                if (_copy && (_copy !== _object)){
                    target.SetChild(relation.reason, _copy, relation.by);
                }
            }
        }
    }

    /**
     * Clears copy information from objects related through the specified relation.
     * Only processes child relations by setting their CopyOf property to null.
     * @param relation - The relation schema defining which relations to clear.
     * @private
     */    
    private _ClearRelation(relation: _schemaRelate){
        if (relation.child == true){
            const _related = this._children[relation.reason];
            if (_related) {
                if (_related instanceof DataObject) {
                    _related.CopyOf = null;
                }
                else {
                    for (const _child of this._chldlist[relation.reason]){
                        _child.CopyOf = null;
                    }
                }
            }            
        }
    }

    /**
     * Returns whether this object and `object` are the same logical row (for example a volatile copy
     * and its original when linked via `CopyOf`).
     *
     * @param object - Other object to compare.
     * @returns `true` when both refer to the same canonical instance after following `CopyOf`.
     */
    IsEqual(object: DataObject): boolean {
        const canonical = (o: DataObject): DataObject => {
            let x = o;
            let up = x.CopyOf as DataObject | null;
            while (up) {
                x = up;
                up = x.CopyOf as DataObject | null;
            }
            return x;
        };

        return canonical(this) === canonical(object);
    }

    /**
     * Gets the original object this instance is a copy of.
     * @returns The `DataObject` instance this is a copy of, or `null` if not a copy.
     */    
    get CopyOf(): DataObject | null {
        return this._copyof;
    }

    /**
     * Sets the original object this instance is a copy of.
     * When set to `null`, removes copy information from related objects.
     * @param value - The `DataObject` instance to associate as the original, or `null` to remove the reference.
     */    
    set CopyOf(value: DataObject | null){
        if (this._copyof != value){
            this._copyof = value;

            if (value == null){     // remove copy information from related objects
                this.data.store.AddObject(this); 

                if (this._schema){
                    for (const relation of this._schema.relate){
                        this._ClearRelation(relation);
                    }
                }
            }
        }
    }

    /**
     * Analyzes this object and its child relationships to determine which objects
     * should be included in a deep copy operation.
     *
     * This method recursively traverses all child relations defined in the schema
     * and adds each relevant object to a set, marking it for copying.
     *
     * @param objnfo - An optional set used to collect the objects to be copied.
     * @returns A `Set` of `DataObject` instances to be deeply copied.
     */   
    protected _AnalyzeCopy(objnfo: Set <DataObject> | null = null): Set <DataObject> {
        if (objnfo == null){
            objnfo = new Set <DataObject> ();
        }
        
        if (!this._schema || objnfo.has(this)){
            return objnfo;  // nothing to do
        }

        objnfo.add(this);     // either the first item or a related one

        for (const relation of this._schema.relate){
            if (relation.child == true){
                const _related = this._children[relation.reason];
                if (_related) {
                    if (_related instanceof DataObject) {
                        _related._AnalyzeCopy(objnfo);
                    }
                    else {
                        for (const _related of this._chldlist[relation.reason] as DataObject[]){
                            _related._AnalyzeCopy(objnfo);
                        }                
                    }
                }
            }            
        }

        return objnfo;
    }

    /**
     * Creates a deep copy of this object, including all its related child objects,
     * based on the schema definition.
     *
     * The method respects copy flags analyzed via `_AnalyzeCopy()` and avoids
     * duplicate copies by storing created objects in the `store` list.
     *
     * @param store - A list used to track and reuse already copied objects.
     * @param objnfo - A set of objects marked for deep copying. If not provided, it is generated by calling `_AnalyzeCopy()`.
     * @returns A new `DataObject` instance that is a deep copy of this object, or `null` on failure.
     */   
    protected _Copy (store: Array<DataObject> = [], objnfo: Set <DataObject> | null = null) : DataObject | null{
        if (!objnfo){   // perform a deep analysis of the objects to be copied
            objnfo = this._AnalyzeCopy();
        }

        const _copy = new (this.constructor as { new(objid: string | null, data: dataService, options: ObjectOptions): DataObject | null })(null, this.data, {});
        if (_copy) {
            _copy._copyof = this;       // establish the relation with the original object             
            store.push(_copy);          // copied objects are added into the store (for reutilization)
            
            if (_copy){
                _copy.Data = GenericUtils.CopyObject(this.Info); 
                _copy._isCatalog = this._isCatalog;
                _copy._toUpdate = this._toUpdate;
                _copy._isLoaded = this._isLoaded;
                _copy._isPartial = this._isPartial;
                _copy._completed = this._completed;

                if (this._schema){
                    // recursivelly perforn the deep-copy operation
                    for (const relation of this._schema.relate){
                        this._CopyRelation(_copy, relation, store, objnfo);
                    }
                }
            }
        }
        
        return _copy;
    }
    
    /**
     * Creates a deep copy of the object, including its relationships.
     * This method must be implemented by derived classes.
     * @param store - A list to track copied objects, avoiding duplicates.
     * @returns A new `DataObject` instance representing the copied object.
     */    
    abstract Copy(store: Array<DataObject>) : DataObject;

    /**
     * Updates all relationship references from a previous object to a new target object.
     * Scans all relationships in the schema and replaces references pointing to the 
     * previous object with references to the new object.
     * 
     * @param prev - The object currently being referenced that should be replaced.
     * @param next - The new object that should replace the previous reference.
     * 
     * @remarks
     * Processes both single (1:1) and multiple (1:N) relationships to maintain 
     * referential integrity during object restoration processes.
     * 
     * @internal
     */    
    private Reparent(prev: DataObject, next: DataObject): void {
        if (!this._schema) {
            return;
        }

        for (const relation of this._schema.relate || []) { 
            const related = this._children[relation.reason]; 
            if (!related){
                continue;
            }
            
            if (related instanceof DataObject){
                const target = related as DataObject;
                if (target == prev){
                    this.SetChild(relation.reason, next, relation.by);
                }
            }            
            else {
                for (const target of Object.values(related) as DataObject[]){
                    if (target == prev){
                        this.DelChild(relation.reason, prev, relation.by)
                        this.AddChild(relation.reason, next, relation.by)
                    }
                }
            }
        }       
    }

    /**
     * Resolves relationships for this object.
     *
     * When the storage has replaced this instance by another one for the same table and objid
     * (a stub created by the factory because a foreign key arrived before the awaiting owner of
     * that objid was resolved: children are synchronized before their parents), every object that
     * still references this instance is handed over to the surviving one. Otherwise the same row
     * would live in two instances. The referrers keep their pending-update flag: the database
     * already holds the link.
     */
    override OnResolve(): void {
        const _next = this.data.store.GetObject(this) as DataObject | null;
        if (_next && (_next !== this)){
            for (const referrer of [...this._related.keys()]){
                if (referrer instanceof DataObject){
                    const _toUpdate = referrer._toUpdate;
                    referrer.Reparent(this, _next);
                    referrer._toUpdate = _toUpdate;
                }
            }
        }

        super.OnResolve();
    }

    /**
     * Outputs debug information with hierarchical indentation based on recursion level.
     * 
     * @param level - The recursion depth level for indentation.
     * @param text - The debug message to output.
     * 
     * @internal
     */    
    private OverwriteLog(level: number, text: string){
        if (GenericUtils.VerboseLogging(5)){
            let log = '[OVERWRITE] ';
            for(let i=0; i < level; i++){
                log += "    ";
            }
            console.log(log + text);
        }
    }

/**
     * Restores a copied object by transferring its data to the original instance and
     * recursively processing all related objects to maintain referential integrity.
     * 
     * @param recursionlevel - Optional recursion depth for debugging. Defaults to 0.
     * @param ignore - Set of objects already processed to prevent duplicate processing.
     * @returns The original object that this copy represents, or this object if not a copy.
     * 
     * @throws Error when object schema is required but missing for a copy object.
     * 
     * @remarks
     * **Process:**
     * 1. Returns immediately if not a copy (CopyOf === null)
     * 2. Checks if object was already processed to avoid duplicates
     * 3. Transfers data, update status, and load status to original object
     * 4. For each relationship: reparents related objects and recursively processes them
     * 5. Updates parent-child references to maintain bidirectional integrity
     * 6. Removes copy from data store and triggers completion events
     * 
     * **Circular References & Duplicates:** The `ignore` Set prevents processing the same
     * object multiple times when it's referenced from multiple paths in the object graph.
     * When an object is encountered again, it returns the already-processed result.
     * 
     * @example
     * ```typescript
     * const copy = original.Copy();
     * copy.Data.someProperty = "new value";
     * const restored = copy.Overwrite(); // restored === original
     * 
     * // With custom ignore set (advanced usage)
     * const processed = new Set<DataObject>();
     * const restored2 = copy2.Overwrite(0, processed);
     * ```
     */ 
    Overwrite(recursionlevel = 0, ignore: Set <DataObject> = new Set <DataObject>): DataObject {
        this.OverwriteLog(recursionlevel, "processing item " + this.table + "@" + this._uuid + " - status: " + (this as any)['status']);
        const copyof = this.CopyOf;
        if (!copyof){
            this.OverwriteLog(recursionlevel, "processing item " + this.table + "@" + this._uuid + " - COMPLETED (not a copy)");
            return this;        // this is not a copy
        }      

        this.OverwriteLog(recursionlevel, "procesing copy..");
        if (!this._schema) {
            throw new Error ("[ERROR] object schema is required for operation.");
        }      

        if (ignore.has(this)){
            this.OverwriteLog(recursionlevel, "processing item " + this.table + "@" + this._uuid + " - COMPLETED (already processed)");
            return copyof || this;  // already processed
        }
        ignore.add(this);

        // replace the source with the data in the copy
        copyof.Data = GenericUtils.CopyObject(this.Info);
        copyof._toUpdate = this._toUpdate;
        copyof._isLoaded = this._isLoaded;
        copyof._isPartial = this._isPartial;
        copyof._completed = this._completed;

        for (const relation of this._schema.relate || []) {
            const related = this._children[relation.reason]; 
            if (!related){
                this.OverwriteLog(recursionlevel, "relation '" + this.table + "[" + relation.reason+ "] (skip)");
                if (relation.direction == '>'){
                    copyof.SetChild(relation.reason, null, relation.by);
                }
            }
            else {
                if (related instanceof DataObject){
                    this.OverwriteLog(recursionlevel, "relation " + this.table + "[" + relation.reason+ "] 1:1 '" + relation.reason + "' (reparent)");
                    const prev = related as DataObject;
                    prev.Reparent(this, copyof);

                    const next =  prev.Overwrite(recursionlevel + 1, ignore)
                    copyof.SetChild(relation.reason, next, relation.by);
                }
                else {
                    // clear the previous list of children in this relation
                    for (const child of copyof._chldlist[relation.reason] || []){
                        copyof.DelChild(relation.reason, child, relation.by);
                    }

                    // replace with the new set of children based in the copy
                    for (const child of Object.values(related)){
                        const prev = child as DataObject;

                        this.OverwriteLog(recursionlevel, "relation " + this.table + "[" + relation.reason+ "] 1:N '" + relation.reason + "' (reparent)");
                        prev.Reparent(this, copyof);

                        const next = prev.Overwrite(recursionlevel + 1, ignore);
                        copyof.AddChild(relation.reason, next, relation.by);
                    }
                }
            }
        }

        this.data.store.DelObject(this);
        this._onOverwrite.next(copyof);

        this.OverwriteLog(recursionlevel, "processing item " + this.table + "@" + this._uuid + " - COMPLETED");

        return copyof;
    }

    /********************************/
    /* DATA UPDATE METHODS          */
    /********************************/

    /**
     * Stores the last state of the object that was updated.
     * Used to prevent redundant updates when the new data matches the current state.
     */    
    private _lastupdate = null; 
    
    /**
     * Determines whether update logs should be printed to the console.
     * If `true`, all update-related logs will be displayed for debugging purposes.
     */    
    private _updatelogs = false;

    /**
     * Logs a message to the console if update logging is enabled.
     *
     * @param log - The message to log to the console.
     */    
    private _ConsoleLog(log: string): void {
        if (this._updatelogs){
            console.info(log);
        }
    }

    /**
     * Determines if the object is eligible for updates.
     * Objects marked for insertion (`_toInsert`) or not marked for updates (`_toUpdate`) can be updated.
     *
     * @returns `true` if the object can be updated; otherwise, `false`.
     */    
    protected get _CanUpdate(): boolean {
        return this._toInsert || !this._toUpdate;     
    }

    /**
     * Compares two data objects to determine if they are equal.
     * The comparison ignores keys that start with an underscore (`_`) and fields like `objid`, `created`, and `updated`.
     *
     * @param info1 - The first data object to compare.
     * @param info2 - The second data object to compare.
     * @returns `true` if the two objects are equal; otherwise, `false`.
     */    
    private _equal(info1: any | null, info2: any | null): boolean {
        let _equal = true;

        const keys1 = info1 ? Object.keys(info1).filter(item => !item.startsWith('_')) : [];
        const keys2 = info2 ? Object.keys(info2).filter(item => !item.startsWith('_')) : [];

        _equal = _equal && keys1.every(_key => {
            return ((['created', 'updated'].includes(_key))) || ((keys2.includes(_key)) && (info1 && info2) && (info1[_key] == info2[_key]));
        })

        _equal = _equal && keys2.every(_key => {
            return ((['created', 'updated'].includes(_key))) || ((keys1.includes(_key)) && (info1 && info2) && (info1[_key] == info2[_key]));
        })

        return _equal;
    }

    /**
     * True when `local` and `current` differ under the same rules as {@link patchValue}.
     */
    private _ValueChanged(local: any, current: any): boolean {
        let _src = current;
        let _dst = local;

        _src = (_src instanceof DataObject) ? (_src.objid || _src._uuid) : _src;
        _dst = (_dst instanceof DataObject) ? (_dst.objid || _dst._uuid) : _dst;

        return (_src !== _dst) || (JSON.stringify(_src) !== JSON.stringify(_dst));
    }

    /**
     * Updates this object with the specified data.
     * @param info - The data to apply to this object.
     * @returns `true` if the object was updated; otherwise, `false`.
     */    
    protected OnUpdate(info: any): boolean {
        this._inCommit = false;

        let _change = false;
        if (this._CanUpdate){
            _change = !this._equal(this._lastupdate, info);
            if (_change){   // update if changes are present
                // Snapshot edits queued after the in-flight commit was built. The server
                // echo must still resolve objid/IsLoaded, but must not clobber those fields
                // (e.g. TICKETPRODUCT.amount decremented by a split before insert ACK).
                const _pending = (this._lastchange || []).slice();

                this._lastupdate = Object.assign({}, info);
    
                this._OnUpdate(info);
        
                this._simplehash = null;
                this._isLoaded = true;
                this._toUpdate = false;
    
                this._waitLoaded.next(true);
                this._waitLoaded.complete();
    
                if (!this.objid || (info['_actn'] == 'do_insert')){
                    if (!this.objid){
                        this._ConsoleLog("[ITEM] resolve: " + info['table'] + "@" + info['objid']);
                        this.OnResolve();    
                    }
                    else {
                        this._ConsoleLog("[ITEM] replace: " + info['table'] + "@" + info['objid']);
                        this.OnReplace(info['_uuid']);
                    }
                }
                else {
                    this._ConsoleLog("[ITEM] update: " + info['table'] + "@" + info['objid']);
                }

                this.DoRefresh(this.table, true);

                const _redirty: Array<{ property: string, value: any }> = [];
                for (const _entry of _pending){
                    const _prop = _entry.property;
                    if (['objid', 'created', 'updated'].includes(_prop)){
                        continue;
                    }

                    if (!this._ValueChanged(_entry.value, this.Info[_prop])){
                        continue;   // same as server echo — not a post-commit edit
                    }

                    this.Info[_prop] = _entry.value;
                    _redirty.push({ property: _prop, value: _entry.value });
                }

                this._lastchange = _redirty;
                if (_redirty.length > 0){
                    this.ToUpdate = true;
                    this.DoRefresh(this.table, true);
                }
            }
            else {
                this._ConsoleLog("[ITEM] ignore: " + info['table'] + "@" + info['objid']);
            }
        }

        return _change;
    }

    /****************************/
    /* BUILD FROM DATABASE      */
    /****************************/

    /**
     * Populates the object with data retrieved from an external source (e.g., a database).
     * This method is used to initialize the object's state and mark it as loaded.
     * 
     * @param data - The data to apply to the object.
     */    
    protected _FromData(data: any): void {
        this._OnUpdate(data);
        this._isLoaded = true;
    }

    /**
     * Builds the object using the provided data.
     * This method is intended for volatile instances only, as they can be initialized
     * with data directly. Non-volatile objects cannot be built using this method.
     * 
     * @param data - The data to apply to the object.
     * @throws Will throw an error if the object is not marked as volatile.
     */
    FromData(data: any): void {  
        if (!this._isVolatile){
            throw new Error("[ERROR]: Only volatile instances can be built from data!")
        }
        else {
            this._FromData(data);
        }
    }

    /********************************/
    /* INTERNAL USE METHODS         */
    /********************************/

    /**
     * Refreshes this object, always triggering an immediate refresh.
     * @param table - The table to refresh.
     */    
    DoRefresh(table:string[] | string, upwards = true): void {
        if (RelatedObject.IsChildRefreshDeferred){
            return;
        }

        const _tables: string[] = Array.isArray(table) ? table : [ table ];

        this._onRefresh.next(_tables);      
        if (upwards){
            this.UpRefresh(_tables, null);
        }
    }
   
    /**
     * Triggers an upward refresh in the object hierarchy.
     */    
    UpRefresh(table: string[], _updates: Set <DataObject> | null): void {
        if (!this._schema){
            return;
        }

        if (_updates == null){
            _updates = new Set <DataObject> ();
        }

        if (_updates.has(this)){
            return;     // already updated
        }
        _updates.add(this);

        for (const _relation of this._schema.relate){
            if (_relation.child == false){
                const _related = this._children[_relation.name] || null;
                if (_related && (_related instanceof RelatedObject)){
                    _related.DoRefresh(table, false);
                    _related.UpRefresh(table, _updates);
                }
            }
        }
    }

    /************************************/
    /* METHODS FOR DERIVED CLASSES      */
    /************************************/

    /**
     * Updates a specific property of an object if its value has changed.
     * This method checks whether the current value of the property differs from
     * the new value, either by direct comparison or by comparing their serialized
     * JSON representations. If a change is detected, the property is updated, and
     * the change is recorded.
     *
     * @param dst - The target object where the property resides.
     * @param property - The name of the property to update.
     * @param value - The new value to assign to the property.
     * @returns `true` if the property was updated; otherwise, `false`.
     */    
    patchValue(dst: any, property: string, value: any): boolean {
        let _src = dst[property];
        let _dst = value;

        _src = (_src instanceof DataObject) ? (_src.objid || _src._uuid) : _src;
        _dst = (_dst instanceof DataObject) ? (_dst.objid || _dst._uuid) : _dst;

        const _change = (_src !== _dst) || (JSON.stringify(_src) !== JSON.stringify(_dst));
        if (_change){
            dst[property] = (typeof(value) !== 'undefined') ? value : null;

            if (!this._lastchange){
                this._lastchange = [];
            }

            this._lastchange.push({
                property: property,
                value: dst[property]
            });
        }

        return _change;
    }
}

